import React from 'react';
import styled from 'styled-components';

import {
  Colors,
  DataStore,
  FontSizes,
  FontFamilies,
  Theme,
  isValidEmail,
} from 'common';

import {
  Button,
  Checkbox,
  Stepper,
  Text,
  TextInput,
  Select,
} from 'common/components';

import { withCommon } from 'common/hocs';

import {
  EventName,
  MaxWidthContainer,
  StepperContainer,
} from 'ui/event/styled';

const Divide = styled.div`
  background-color: ${Theme.EventIdentity.DividerColor};
  height: 1px;
  width: 100%;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 0 0 0;
`;

const ButtonSpace = styled.div`
  width: 20px;
`;

const FullWidthContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const CustomFieldContainer = styled.div`
  margin: 15px 0;
  width: 100%;
`;

const CustomFieldLabel = styled(Text)`
  display: block;
  padding: 0 0 5px 0;
  
  ${props => props.required && `
    &:after {
      content: ' *';
      color: ${Colors.Error};
    }
  `}
`;

const EventCustomFieldsUI = props => {

  const [state, setState] = React.useState(() => {
    const event = DataStore.get('selectedEvent') || {};
    const authenticatedContact = DataStore.get('LOGGED_IN_EMAIL');
    const customFields = {};

    // Prepopulate email and phone fields with authenticated contact
    if (event.custom_fields && authenticatedContact) {
      event.custom_fields.forEach(field => {
        if (field.type === 'email' && isValidEmail(authenticatedContact)) {
          customFields[field.id] = {
            ...field,
            value: authenticatedContact
          };
        } else if (field.type === 'tel' && !isValidEmail(authenticatedContact)) {
          // If not an email, assume it's a phone number
          customFields[field.id] = {
            ...field,
            value: authenticatedContact
          };
        }
      });
    }

    return {
      event,
      customFields,
    };
  });

  const validateCustomField = (field, value) => {
    if (field.required && (!value || (Array.isArray(value) && !value.length) || (typeof value === 'string' && !value.trim()))) {
      return `${field.label} is required`;
    }

    if (!value || (typeof value === 'string' && !value.trim())) return null; // Don't validate empty optional fields

    switch (field.type) {
      case 'email':
        return isValidEmail(value) ? null : `${field.label} must be a valid email`;
      
      case 'tel':
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        return phoneRegex.test(value) ? null : `${field.label} must be a valid phone number`;
      
      case 'number':
        return !isNaN(value) ? null : `${field.label} must be a valid number`;
      
      default:
        return null;
    }
  };

  const validateCustomFields = () => {
    if (!state.event?.custom_fields?.length) return true;

    const errors = state.event.custom_fields.reduce((acc, field) => {
      const error = validateCustomField(field, state.customFields?.[field.id]?.value);
      if (error) acc.push(error);
      return acc;
    }, []);

    if (errors.length > 0) {
      props.common.showToast(errors.join('. '));
      return false;
    }

    return true;
  };

  const onCustomFieldChange = (field, value) => {
    if (!field) return;
    if (!field.id) field.id = new Date().getTime();

    setState(prevState => ({
      ...prevState,
      customFields: {
        ...prevState.customFields,
        [field.id]: {
          ...field,
          value: value
        }
      }
    }));
  };

  const renderCustomField = (field) => {
    const value = state.customFields?.[field.id]?.value || '';
    const commonProps = {
      width: '100%',
      value: value,
      onChange: (value) => onCustomFieldChange(field, value),
      placeholder: field.placeholder || `Enter ${field.label.toLowerCase()}...`,
    };
  
    switch (field.type) {
      case 'textarea':
        return (
          <TextInput
            {...commonProps}
            multiline={true}
          />
        );
  
      case 'number':
        return (
          <TextInput
            {...commonProps}
            type="number"
          />
        );
  
      case 'email':
        return (
          <TextInput
            {...commonProps}
            type="email"
          />
        );
  
      case 'tel':
        return (
          <TextInput
            {...commonProps}
            type="tel"
          />
        );
  
      case 'checkbox':
        return (
          <Checkbox
            checked={!!value}
            label={field.placeholder || ''}
            onClick={(checked) => onCustomFieldChange(field.id, checked)}
          />
        );
  
      case 'select':
        return (
          <Select
            {...commonProps}
            options={field.options.map(opt => ({
              value: opt,
              label: opt
            }))}
            placeholder={`Select ${field.label.toLowerCase()}...`}
          />
        );
  
      default: // text
        return (
          <TextInput
            {...commonProps}
            type="text"
          />
        );
    }
  };

  const renderCustomFields = () => {
    if (!state.event?.custom_fields?.length) return null;

    return state.event.custom_fields.map((field, index) => (
      <CustomFieldContainer key={`custom-field-${index}`}>
        <CustomFieldLabel
          fontSize={FontSizes.Smaller}
          required={field.required}>
          {field.label}
        </CustomFieldLabel>
        {renderCustomField(field)}
      </CustomFieldContainer>
    ));
  };

  const onSubmitClick = () => {
    if (!validateCustomFields()) {
      return;
    }

    DataStore.set('selectedEvent', {
      ...state.event,
      custom_fields: Object.values(state.customFields || {})
    });

    props.common.navigate('/event/payment');
  };

  const onBackClick = () => {
    props.common.history.goBack();
  };

  return (
    <>
      <Text
        maxWidth={'1100px'}
        margin={'0 auto'}
        width={'100%'}
        display={'block'} 
        padding={'20px 15px 0 15px'} 
        fontSize={FontSizes.Small}
        color={Theme.Text.Color}
      >
        <span style={{cursor: 'pointer'}} onClick={() => props.common.navigate(-1)}>← Back</span>
      </Text>

      <EventName>{state.event.name}</EventName>

      <Text display={'block'} padding={'0 0 5px 0'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.location_display}</Text>

      <Text display={'block'} padding={'0 0 20px 0'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.displayDate}</Text>

      <FullWidthContainer>

        <StepperContainer>
          <Stepper
            items={[{text: 'ORDER'}, {text: 'DETAILS'}, {text: 'PAYMENT'}]}
            selectedIndex={1}
          />
        </StepperContainer>

        <MaxWidthContainer>

          <Text
            display={'block'}
            fontFamily={FontFamilies.Bold}
            fontSize={FontSizes.Big}>
              Enter Details
          </Text>

          <Text
            display={'block'}
            fontSize={FontSizes.Smaller}
            padding={'0 0 15px 0'}>
              Please provide the following information to complete your ticket purchase.
          </Text>

          <Divide/>

          {renderCustomFields()}

        </MaxWidthContainer>

        <div style={{ flex: 1, minHeight: props.common.isWidget ? '30px' : 'auto' }} />

        <MaxWidthContainer padding={'0'}>

          <ActionContainer>

            <MaxWidthContainer>

              <Divide/>

              <ActionButtonContainer>

                <Button onClick={onBackClick} backgroundColor={Theme.Button.Secondary.Background}>Back</Button>

                <ButtonSpace/>

                <Button onClick={onSubmitClick}>Continue</Button>

              </ActionButtonContainer>

            </MaxWidthContainer>

          </ActionContainer>

        </MaxWidthContainer>

      </FullWidthContainer>

    </>
  );
};

export const EventCustomFields = withCommon(
  EventCustomFieldsUI, {
    showLoadingOnLoad: false
  }
); 